import * as React from "react";
import { useRef, useEffect, useState } from "react";
import { Textarea, type TextareaProps } from "./textarea";

export interface ControlledTextareaProps extends Omit<TextareaProps, 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
}

/**
 * ControlledTextarea component that maintains cursor position during value updates.
 * This prevents the cursor from jumping to the end when the value is updated externally.
 */
export const ControlledTextarea = React.forwardRef<
  HTMLTextAreaElement,
  ControlledTextareaProps
>(({ value = "", onChange, onFocus, onBlur, ...props }, forwardedRef) => {
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const ref = (forwardedRef as React.MutableRefObject<HTMLTextAreaElement>) || internalRef;
  
  // Track cursor position
  const [cursorPosition, setCursorPosition] = useState<{
    start: number;
    end: number;
  } | null>(null);
  
  // Track focus state
  const [isFocused, setIsFocused] = useState(false);

  // Handle change events and track cursor position
  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const textarea = e.target;
    
    // Store the cursor position immediately after the change
    setCursorPosition({
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
    });
    
    // Call the original onChange handler with the value
    onChange?.(e.target.value);
  };

  // Handle focus events
  const handleFocus: React.FocusEventHandler<HTMLTextAreaElement> = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  // Handle blur events
  const handleBlur: React.FocusEventHandler<HTMLTextAreaElement> = (e) => {
    setIsFocused(false);
    setCursorPosition(null); // Clear cursor position on blur
    onBlur?.(e);
  };

  // Restore cursor position after value updates
  useEffect(() => {
    const textarea = typeof ref === 'function' ? null : ref.current;
    
    if (textarea && isFocused && cursorPosition) {
      // Use requestAnimationFrame to ensure the DOM has updated
      requestAnimationFrame(() => {
        // Ensure the cursor position is within the text bounds
        const textLength = textarea.value.length;
        const start = Math.min(cursorPosition.start, textLength);
        const end = Math.min(cursorPosition.end, textLength);
        
        // Restore the cursor position
        textarea.setSelectionRange(start, end);
      });
    }
  }, [value, cursorPosition, isFocused, ref]);

  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    />
  );
});

ControlledTextarea.displayName = "ControlledTextarea";